import React from 'react';

const Section3PromptEngineering = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-rose-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">✍️</span>
      <span className="bg-gradient-to-r from-rose-600 to-red-600 bg-clip-text text-transparent">
        Prompt Engineering
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">Beyond API calls: multi-layer validation, versioning & cultural sensitivity</p>

    {/* Validation layers — horizontal flow */}
    <div className="bg-gradient-to-br from-rose-50/50 to-red-50/50 rounded-2xl p-6 border border-rose-200/30 mb-6">
      <h3 className="text-base font-bold text-rose-600 uppercase tracking-wider mb-5 text-center">5-Layer Theme Validation Pipeline</h3>
      <div className="flex flex-wrap md:flex-nowrap items-center justify-center gap-2">
        {[
          { layer: '1', label: 'Input Sanitization', desc: 'Trim, normalize, validate text length and image format', color: 'rose' },
          { layer: '2', label: 'Wedding Relevance Check', desc: 'AI validates if text/image is wedding-related. Rejects with clear error if not', color: 'pink' },
          { layer: '3', label: 'RAG Context Injection', desc: 'Top 3 similar themes from 200+ embeddings appended to prompt', color: 'red' },
          { layer: '4', label: 'AI Generation', desc: 'A/B test assigns prompt variant, model-specific API call with enhanced prompt', color: 'orange' },
          { layer: '5', label: 'Output Validation', desc: 'Parse JSON, verify required fields, 5-dimension quality scoring, fallback to generic template if malformed', color: 'amber' },
        ].map((step, i) => (
          <React.Fragment key={i}>
            <div className={`bg-white rounded-xl p-3 shadow-md border border-${step.color}-200/50 text-center min-w-[140px] flex-1 hover:scale-105 transition-transform duration-300`}>
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-${step.color}-500 to-${step.color}-600 text-white text-xs font-bold mb-2`}>{step.layer}</span>
              <p className="text-sm font-bold text-gray-800">{step.label}</p>
              <p className="text-xs text-gray-400 mt-1">{step.desc}</p>
            </div>
            {i < 4 && <span className="text-rose-300 font-bold hidden md:block">→</span>}
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Prompt strategies */}
    <div className="bg-gradient-to-br from-rose-50/50 to-pink-50/50 rounded-2xl p-6 border border-rose-200/30 mb-6">
      <h3 className="text-base font-bold text-rose-600 uppercase tracking-wider mb-5 text-center">A/B Tested Prompt Strategies</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Dream Generator variants */}
        <div>
          <p className="text-sm font-bold text-gray-700 mb-3 flex items-center"><span className="mr-2">🎨</span>Dream Wedding Generator</p>
          <div className="space-y-2">
            {[
              { variant: 'A', name: 'Few-Shot', desc: 'Includes example themes in the prompt to guide output structure. Most predictable results.', tag: 'Baseline' },
              { variant: 'B', name: 'Context-Aware', desc: 'Injects RAG-matched similar themes + user\'s saved preferences into the prompt. Richer, more personalized output.', tag: 'Current' },
              { variant: 'C', name: 'Hybrid Reasoning', desc: 'Combines chain-of-thought reasoning with few-shot examples and RAG context. Highest quality but most expensive per request.', tag: 'Experimental' },
            ].map((v, i) => (
              <div key={i} className="bg-white/80 rounded-lg p-3 border border-rose-100/50 flex items-start">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-rose-500 to-pink-500 text-white mr-3 mt-0.5 flex-shrink-0">{v.variant}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">{v.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">{v.tag}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Moodboard variants */}
        <div>
          <p className="text-sm font-bold text-gray-700 mb-3 flex items-center"><span className="mr-2">📸</span>Moodboard Matcher</p>
          <div className="space-y-2">
            {[
              { variant: 'A', name: 'Few-Shot', desc: 'Includes example image analyses to guide the model\'s output format. Consistent but generic.', tag: 'Baseline' },
              { variant: 'B', name: 'Theatrical', desc: 'Prompts for rich, evocative language with emotional depth. Most engaging descriptions.', tag: 'Current' },
              { variant: 'C', name: 'Professional', desc: 'Optimized for concise, structured output. Fastest response time, lowest cost per request.', tag: 'Experimental' },
            ].map((v, i) => (
              <div key={i} className="bg-white/80 rounded-lg p-3 border border-rose-100/50 flex items-start">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-rose-500 to-pink-500 text-white mr-3 mt-0.5 flex-shrink-0">{v.variant}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">{v.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">{v.tag}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Context injection & engineering details */}
    <div className="bg-gradient-to-r from-rose-50/80 to-red-50/80 rounded-2xl p-5 border border-rose-200/30">
      <h3 className="text-base font-bold text-rose-600 uppercase tracking-wider mb-3">🎯 Prompt Intelligence</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'AI-powered input validation', a: 'Before generation, a separate AI call (temperature 0.1) validates if the text/image is wedding-related. Non-wedding inputs are rejected with a clear error, not silently ignored.' },
          { q: 'RAG context injection', a: 'Top 3 similar themes from 200+ embeddings are appended to the prompt before generation. Dream Wedding Generator uses text similarity; Moodboard Matcher uses a search query built from detected colors and styles.' },
          { q: 'Structured output enforcement', a: 'Every prompt enforces a strict JSON schema with required fields: theme name, description, colors, styles, key elements, mood, season, formality. Malformed responses fall back to a generic theme template.' },
          { q: 'Per-feature prompt versioning', a: 'Dream Wedding Generator and Moodboard Matcher each have independent prompt version libraries. A/B testing assigns variants per experiment. Prompt selection happens server-side before the model call.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-rose-100/50">
            <p className="text-sm font-bold text-rose-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section3PromptEngineering;
